import { useLayoutEffect, useRef, useState } from "react";
import { PencilIcon, UploadIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  cn,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  V2Dialog,
} from "web-design-system";
import { useUpdateDistributorBranding } from "creso-core/hooks";
import type { DistributorBranding } from "creso-core/types";
import { handleError } from "@/utils/error";
import { ScaledFrame } from "../preview/scaled-frame";
import { BrandingPreviewValues } from "../preview/types";
import { WebLoginPreview } from "../preview/web-login-preview";
import { BannerCropEditor } from "./banner-crop-editor";
import { BannerImage } from "./banner-image";
import {
  BannerUpload,
  BannerView,
  DEFAULT_BANNER_CROP,
  exportCroppedBanner,
  loadBannerUpload,
} from "./crop";
import {
  DEFAULT_LOGIN_BANNER,
  getSavedLoginBannerUrl,
  getStockLoginBanner,
  STOCK_LOGIN_BANNERS,
  StockLoginBanner,
  stockLoginBannerUrl,
} from "./banners";

// Size the login preview is laid out at before it is scaled to fit
const PREVIEW_WIDTH = 1168;
const PREVIEW_HEIGHT = 584;

type LoginBannerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branding?: DistributorBranding;
  previewValues: BrandingPreviewValues;
};

export function LoginBannerDialog({
  open,
  onOpenChange,
  ...props
}: LoginBannerDialogProps) {
  return (
    <V2Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(calc(100dvh-2rem),720px)] w-[min(calc(100dvw-2rem),840px)] max-w-none flex-col gap-0 overflow-hidden p-0">
        <LoginBannerPicker {...props} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </V2Dialog>
  );
}

function LoginBannerPicker({
  branding,
  previewValues,
  onClose,
}: Omit<LoginBannerDialogProps, "open" | "onOpenChange"> & {
  onClose: () => void;
}) {
  const { mutateAsync: updateDistributorBranding } =
    useUpdateDistributorBranding();
  const savedUrl = getSavedLoginBannerUrl(branding);
  const savedStockBanner = savedUrl ? getStockLoginBanner(savedUrl) : null;
  // A saved stock banner selects its own tile; any other saved image gets the custom tile
  const savedCustomUrl = savedStockBanner ? null : savedUrl;
  const savedSelection =
    savedStockBanner ?? (savedCustomUrl ? "custom" : DEFAULT_LOGIN_BANNER);

  const [selected, setSelected] = useState<StockLoginBanner | "custom">(
    savedSelection
  );
  const [upload, setUpload] = useState<BannerUpload | null>(null);
  const uploadUrl = useObjectUrl(upload?.file);
  const [crop, setCrop] = useState(DEFAULT_BANNER_CROP);
  const [isCropping, setIsCropping] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  let customBanner: BannerView | null = null;
  if (upload && uploadUrl) {
    customBanner = {
      url: uploadUrl,
      framing: { crop, imageAspectRatio: upload.width / upload.height },
    };
  } else if (savedCustomUrl) {
    customBanner = { url: savedCustomUrl };
  }

  const previewBanner: BannerView =
    selected === "custom"
      ? (customBanner ?? { url: stockLoginBannerUrl(DEFAULT_LOGIN_BANNER) })
      : { url: stockLoginBannerUrl(selected) };

  const hasChanges =
    selected !== savedSelection || (selected === "custom" && !!upload);

  const onFilePicked = async (file: File) => {
    try {
      setUpload(await loadBannerUpload(file));
      setCrop(DEFAULT_BANNER_CROP);
      setSelected("custom");
      setIsCropping(true);
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const onSave = async () => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      if (selected !== "custom") {
        // A stock banner is saved by its URL
        formData.append("background_image", stockLoginBannerUrl(selected));
      } else if (upload) {
        formData.append(
          "background_image",
          await exportCroppedBanner(upload, crop)
        );
      }
      await updateDistributorBranding(formData);
      toast.success("Login banner updated");
      onClose();
    } catch (error) {
      handleError(error, "Failed to update login banner");
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogHeader className="space-y-2 border-b border-border px-6 py-5">
        <DialogTitle className="text-lg!">Client web app login</DialogTitle>
        <DialogDescription>
          Show your brand right from the login screen
        </DialogDescription>
      </DialogHeader>

      <div className="flex-1 min-h-0 bg-secondary px-10 py-6">
        <ScaledFrame width={PREVIEW_WIDTH} height={PREVIEW_HEIGHT}>
          <WebLoginPreview
            {...previewValues}
            loginBanner={previewBanner}
            width={PREVIEW_WIDTH}
          />
        </ScaledFrame>
      </div>

      <div className="flex flex-col gap-4 justify-center border-t border-border px-10 py-6">
        <span className="font-semibold text-sm">Login banner</span>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          // Kept empty so the same file can be picked again
          value=""
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onFilePicked(file);
          }}
        />

        {isCropping && upload && uploadUrl ? (
          <BannerCropEditor
            upload={upload}
            imageUrl={uploadUrl}
            crop={crop}
            onCropChange={setCrop}
            onReplace={() => fileInput.current?.click()}
            onDone={() => setIsCropping(false)}
          />
        ) : (
          // The tiles share one row, shrinking when they don't fit at full size
          <div className="flex items-start justify-center gap-4">
            {STOCK_LOGIN_BANNERS.map((banner, index) => (
              <BannerTile
                key={banner}
                label={`Banner ${index + 1}`}
                banner={{ url: stockLoginBannerUrl(banner) }}
                selected={selected === banner}
                onSelect={() => setSelected(banner)}
              />
            ))}

            {customBanner ? (
              <BannerTile
                label={upload ? "Your image" : "Current banner"}
                banner={customBanner}
                selected={selected === "custom"}
                onSelect={() => setSelected("custom")}
              >
                {/* Only the label is clickable, so the rest of the tile still selects it */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/80 opacity-0 transition-opacity group-hover:opacity-100 group-has-focus-visible:opacity-100">
                  <button
                    type="button"
                    className="pointer-events-auto flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      if (upload) {
                        setSelected("custom");
                        setIsCropping(true);
                      } else {
                        fileInput.current?.click();
                      }
                    }}
                  >
                    <PencilIcon className="size-4" />
                    Edit
                  </button>
                </div>
              </BannerTile>
            ) : (
              // Wrapped so the border doesn't make this tile larger than the others
              <div className="min-w-0 max-w-40 flex-1">
                <button
                  type="button"
                  className="flex w-full aspect-[5/4] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border transition-colors hover:bg-secondary"
                  onClick={() => fileInput.current?.click()}
                >
                  <UploadIcon className="size-5" />
                  <span className="text-sm font-semibold underline underline-offset-4">
                    Upload image
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <DialogFooter className="gap-2 border-t border-border px-7 py-4 sm:space-x-0">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogFooter>
    </>
  );
}

/** Object URL for a file, released when the file changes or the component unmounts */
function useObjectUrl(file?: File) {
  const [url, setUrl] = useState<string | null>(null);

  // Layout effect, so the URL is ready before the upload is first painted
  useLayoutEffect(() => {
    if (!file) return undefined;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [file]);

  return url;
}

function BannerTile({
  label,
  banner,
  selected,
  onSelect,
  children,
}: React.PropsWithChildren<{
  label: string;
  banner: BannerView;
  selected: boolean;
  onSelect: () => void;
}>) {
  return (
    <div
      className={cn(
        "group relative min-w-0 max-w-40 flex-1 overflow-hidden rounded-2xl ring-slate-500 has-focus-visible:ring-primary",
        selected ? "ring-4" : "has-focus-visible:ring-2"
      )}
    >
      <button
        type="button"
        aria-label={label}
        aria-pressed={selected}
        className="block w-full outline-none"
        onClick={onSelect}
      >
        <BannerImage banner={banner} className="aspect-[5/4]" />
      </button>
      {children}
    </div>
  );
}
